import { isClientSignOutInProgress } from "./signOutGate";

/**
 * Checks if an error is a network-related error (fetch failure, DNS, timeout, etc.).
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const err = error as Record<string, unknown>;
  // An error carrying an HTTP status came from a response: the request reached
  // the server, so it is not a network failure whatever its message says.
  if (typeof err.status === "number") return false;
  const message = typeof err.message === "string" ? err.message : "";

  return (
    message.includes("Failed to fetch") ||
    message.includes("NetworkError") ||
    message.includes("Network request failed") ||
    message.includes("ERR_NETWORK_IO_SUSPENDED") ||
    message.includes("ERR_SOCKET_NOT_CONNECTED") ||
    message.includes("net::ERR_") ||
    err.name === "TypeError" ||
    err.code === "ECONNABORTED" ||
    err.code === "ERR_NETWORK" ||
    err.code === "NETWORK_ERROR" ||
    err.code === "ENOTFOUND" ||
    err.code === "ETIMEDOUT"
  );
}

/** True when a request was blocked because client sign-out is in progress. */
export function isSignOutAbortedError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === "AbortError" &&
    error.message === "Sign-out in progress"
  );
}

/**
 * True when a mutation/query error should not be logged or surfaced during sign-out teardown.
 */
export function shouldSuppressClientSignOutError(error: unknown): boolean {
  if (isClientSignOutInProgress()) return true;
  return isSignOutAbortedError(error);
}

/**
 * Checks if an error is an authentication error (401 / UNAUTHORIZED).
 */
export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { status?: number; code?: string };
  return err.status === 401 || err.code === "UNAUTHORIZED";
}

/**
 * Checks if the API reported no active session (404 / no active session message).
 * Retrying sync in this state wastes time and can stall initial page load.
 */
export function isNoActiveSessionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { status?: number; message?: string };
  if (err.status === 404) return true;
  return (
    typeof err.message === "string" &&
    err.message.toLowerCase().includes("no active session")
  );
}

/**
 * Checks if the error is "OAuth token not available" / token_not_found.
 * This is an expected state (user needs to reconnect), not a real error.
 */
export function isOAuthTokenUnavailable(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { code?: string; message?: string };
  return (
    err.code === "token_not_found" ||
    (typeof err.message === "string" &&
      err.message.toLowerCase().includes("oauth token not available"))
  );
}

/**
 * Formats an error into a short string for log messages.
 * Ensures the message is never empty so log output is always meaningful
 * (avoids "{}" when the error object does not serialize well in the browser).
 */
export function formatErrorForLog(error: unknown): string {
  if (error == null) return "null";
  if (typeof error === "object" && error !== null) {
    const obj = error as Record<string, unknown>;
    if (typeof obj.message === "string" && obj.message) return obj.message;
    if (typeof obj.status === "number") return `status ${obj.status}`;
    if (typeof obj.code === "string") return obj.code;
  }
  if (error instanceof Error && error.message) return error.message;
  return String(error);
}

let recentNetworkErrors = 0;
let lastNetworkErrorTime = 0;

/**
 * Tracks network errors and detects probable sleep/wake bursts.
 * Returns `true` when multiple network errors fire within 5 seconds,
 * which typically indicates the device is resuming from sleep.
 */
export function trackNetworkError(): boolean {
  const now = Date.now();
  if (now - lastNetworkErrorTime < 5000) {
    recentNetworkErrors++;
  } else {
    recentNetworkErrors = 1;
  }
  lastNetworkErrorTime = now;
  return recentNetworkErrors > 2;
}
